import { useMemo, useCallback, useRef, useEffect } from 'react';

import { useSizingState } from '../hooks/use-sizing-state';

import type { MeasurementSheetType } from '@/visualization/domain/sizing/templates/types';

// ============================================================================
// Types
// ============================================================================

export interface SvgMeasurementMapping {
    id: string;
    type: MeasurementSheetType;
}

export interface SvgSizingSheetProps {
    /** Raw SVG string content (e.g. imported with `?raw`) */
    svgContent: string;
    /** Resolve a `data-key` placeholder to a measurement mapping */
    getMeasurementFromPlaceholder: (placeholder: string) => SvgMeasurementMapping | null;
    /** When true, disables interactions and renders all values with uniform styling */
    readOnly?: boolean;
}

// ============================================================================
// Helpers
// ============================================================================

interface TextElementEntry {
    textEl: SVGTextElement;
    measurementId: string;
    type: MeasurementSheetType;
}

function formatMeasurementValue(value: number, type: MeasurementSheetType): string {
    if (value === null || value === undefined || isNaN(value)) {
        return 'N/A';
    }

    switch (type) {
        case 'length':
        case 'diameter':
            return `${value.toFixed(1)} mm`;
        case 'angle':
            return `${value.toFixed(1)}°`;
        case 'conicity':
            return `${value.toFixed(1)}%`;
        default:
            return value.toFixed(1);
    }
}

function fillColor(type: MeasurementSheetType): string {
    switch (type) {
        case 'diameter':
            return '#009FE3';
        case 'length':
            return '#00A993';
        case 'angle':
            return '#4C3087';
        case 'conicity':
            return '#009FE3';
        default:
            return '#333333';
    }
}

function getMeasurementIdFromTarget(target: EventTarget | null): string | null {
    if (!(target instanceof Element)) return null;
    const textEl = target.tagName === 'text' ? target : target.closest('text');
    return textEl?.getAttribute('data-measurement-id') ?? null;
}

/**
 * Parse a raw SVG string and return the SVG element along with
 * references to every measurement text element inside the "Values" group.
 */
function parseSvg(
    rawSvg: string,
    resolvePlaceholder: (key: string) => SvgMeasurementMapping | null,
    readOnly: boolean
): { svgEl: SVGSVGElement; entries: TextElementEntry[] } | null {
    const parser = new DOMParser();
    const doc = parser.parseFromString(rawSvg, 'image/svg+xml');
    const svgEl = doc.querySelector('svg');
    if (!svgEl) return null;

    // Make the SVG responsive
    svgEl.setAttribute('width', '100%');
    svgEl.setAttribute('height', '100%');
    svgEl.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    svgEl.style.display = 'block';

    const entries: TextElementEntry[] = [];
    const valuesGroup = doc.getElementById('Values');

    if (valuesGroup) {
        valuesGroup.querySelectorAll<SVGTextElement>('text').forEach((textEl) => {
            const key = textEl.getAttribute('data-key');
            if (!key) return;

            const mapping = resolvePlaceholder(key);
            if (!mapping) return;

            textEl.style.fill = fillColor(mapping.type);
            textEl.style.fontFamily = "'JetBrains Mono', monospace";

            if (readOnly) {
                textEl.style.cursor = 'default';
                textEl.style.opacity = '1';
            } else {
                textEl.style.cursor = 'pointer';
                textEl.style.transition = 'opacity 0.15s ease';
                textEl.setAttribute('data-measurement-id', mapping.id);
                textEl.setAttribute('data-measurement-type', mapping.type);
            }

            entries.push({ textEl, measurementId: mapping.id, type: mapping.type });
        });
    }

    return { svgEl, entries };
}

// ============================================================================
// Component
// ============================================================================

export function SvgSizingSheet({
    svgContent,
    getMeasurementFromPlaceholder,
    readOnly = false,
}: SvgSizingSheetProps) {
    const sizing = useSizingState();
    const hoveredMeasurementIdRef = useRef<string | null>(null);
    const textEntriesRef = useRef<TextElementEntry[]>([]);
    const svgElRef = useRef<SVGSVGElement | null>(null);
    const measurementMapRef = useRef<Map<string, { value: number; unit: string }>>(new Map());

    // ── Build a lookup of measurement values ────────────────────────────
    const measurementMap = useMemo(() => {
        const map = new Map<string, { value: number; unit: string }>();
        if (!sizing) return map;

        const { measurements, diameterGroups, minMaxDiameters, customDiameters } = sizing;

        const allMeasurements = [
            ...measurements.lengths,
            ...measurements.angles,
            ...measurements.derived,
            ...minMaxDiameters,
            ...customDiameters,
            ...diameterGroups.flatMap((g) => [g.main, ...g.offsets]),
        ];

        allMeasurements.forEach((m) => map.set(m.id, m));
        return map;
    }, [sizing]);
    measurementMapRef.current = measurementMap;

    // ── Imperative updaters ─────────────────────────────────────────────
    const updateTextValues = useCallback(() => {
        const map = measurementMapRef.current;
        for (const { textEl, measurementId, type } of textEntriesRef.current) {
            const measurement = map.get(measurementId);
            textEl.textContent = measurement ? formatMeasurementValue(measurement.value, type) : '';
        }
    }, []);

    const applyHighlightStyles = useCallback(() => {
        if (readOnly) return;

        const selectedId = sizing?.selectedMeasurementId ?? null;
        const hoveredId = hoveredMeasurementIdRef.current;

        for (const { textEl, measurementId } of textEntriesRef.current) {
            const isHighlighted = measurementId === selectedId || measurementId === hoveredId;
            textEl.style.fontWeight = isHighlighted ? 'bold' : '';
            textEl.style.opacity = isHighlighted ? '1' : '0.6';
        }
    }, [readOnly, sizing?.selectedMeasurementId]);

    // ── Callback ref: parse SVG once when the container mounts ──────────
    const svgContainerRef = useCallback(
        (container: HTMLDivElement | null) => {
            if (!container) {
                svgElRef.current?.remove();
                svgElRef.current = null;
                textEntriesRef.current = [];
                return;
            }

            const result = parseSvg(svgContent, getMeasurementFromPlaceholder, readOnly);
            if (!result) return;

            svgElRef.current = result.svgEl;
            textEntriesRef.current = result.entries;
            container.appendChild(result.svgEl);

            // Apply initial values and highlights synchronously
            updateTextValues();
            applyHighlightStyles();
        },
        [svgContent, getMeasurementFromPlaceholder, readOnly, updateTextValues, applyHighlightStyles]
    );

    // ── Re-apply when data changes after mount ──────────────────────────
    useEffect(() => {
        updateTextValues();
    }, [measurementMap, updateTextValues]);

    useEffect(() => {
        applyHighlightStyles();
    }, [applyHighlightStyles]);

    // ── Event handlers (only used in interactive mode) ───────────────────
    const handleMouseMove = useCallback(
        (e: React.MouseEvent) => {
            const id = getMeasurementIdFromTarget(e.target);
            if (id !== hoveredMeasurementIdRef.current) {
                hoveredMeasurementIdRef.current = id;
                applyHighlightStyles();
            }
        },
        [applyHighlightStyles]
    );

    const handleMouseLeave = useCallback(() => {
        hoveredMeasurementIdRef.current = null;
        applyHighlightStyles();
    }, [applyHighlightStyles]);

    const handleClick = useCallback(
        (e: React.MouseEvent) => {
            const id = getMeasurementIdFromTarget(e.target);
            if (!id || !sizing) return;

            if (sizing.selectedMeasurementId === id) {
                sizing.deselectMeasurement();
            } else {
                sizing.selectMeasurement(id);
            }

            sizing.navigateToMeasurement(id);
        },
        [sizing]
    );

    if (!sizing) return null;

    return (
        <div className="w-full h-full flex">
            <div className="overflow-hidden flex-1">
                <div
                    ref={svgContainerRef}
                    className="w-full h-full bg-stone-100"
                    onMouseMove={readOnly ? undefined : handleMouseMove}
                    onMouseLeave={readOnly ? undefined : handleMouseLeave}
                    onClick={readOnly ? undefined : handleClick}
                />
            </div>
        </div>
    );
}
